import { useTranslation } from 'react-i18next';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { VoiceProfileSample } from '@utils/types/organization/voice-profile.ts';
import { formatDate } from '@utils/date.ts';

interface Props {
  samples: VoiceProfileSample[];
  enrolledCount: number;
  maxSamples: number;
  atLimit: boolean;
  selectedId: number | null;
  onSelect: (profileId: number | null) => void;
  disabled?: boolean;
}

/** Choose which existing sample a new one replaces.
 *
 * Replacement is deliberately opt-in and explicit: adding keeps every previous
 * sample, and overwriting one has to name it. At the 20-sample limit adding is
 * impossible, so this is the only way forward — the picker says that rather
 * than leaving a disabled button unexplained. */
export const VoiceSampleReplacePicker = ({
  samples,
  enrolledCount,
  maxSamples,
  atLimit,
  selectedId,
  onSelect,
  disabled = false
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  if (!samples.length) {
    return (
      <Text as="p" size="xs" tone="muted">
        {t('voice-sample-count-of-max', {
          count: enrolledCount,
          max: maxSamples
        })}
      </Text>
    );
  }

  const describe = (sample: VoiceProfileSample) => {
    const name = sample.label || t('voice-sample-unlabelled');
    const date = sample.created_at ? formatDate(sample.created_at) : null;
    return date ? `${name} · ${date}` : name;
  };

  return (
    <div
      className="space-y-2 rounded-md border p-3"
      style={{ borderColor: getColour('border') }}
    >
      <Text as="p" size="xs" tone="muted">
        {t('voice-sample-count-of-max', {
          count: enrolledCount,
          max: maxSamples
        })}
      </Text>

      {atLimit && (
        <Text as="p" size="sm" tone="danger">
          {t('voice-at-sample-limit', { max: maxSamples })}
        </Text>
      )}

      <fieldset className="space-y-1" disabled={disabled}>
        <legend className="sr-only">{t('voice-replace-which-sample')}</legend>

        {/* Adding is the default, and is not offered at the limit. */}
        {!atLimit && (
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="voice-replace-target"
              checked={selectedId === null}
              onChange={() => onSelect(null)}
              disabled={disabled}
            />
            <span>{t('voice-add-as-new-sample')}</span>
          </label>
        )}

        {samples.map(sample => (
          <label
            key={sample.id}
            className="flex items-center gap-2 text-sm cursor-pointer"
          >
            <input
              type="radio"
              name="voice-replace-target"
              checked={selectedId === sample.id}
              onChange={() => onSelect(sample.id)}
              disabled={disabled}
            />
            <span>
              {t('voice-replace-named', { sample: describe(sample) })}
            </span>
          </label>
        ))}
      </fieldset>
    </div>
  );
};
